/**
 * A fixed-timestep simulation driven by requestAnimationFrame.
 *
 * The loop used to hand `update` the real elapsed time, clamped at a fifteenth
 * of a second. That is the ordinary advice and it was wrong here for two
 * reasons, one of which cost most of an evening.
 *
 * The cheap reason is that a variable step makes the game a different game on a
 * different machine: a cart cornering at 144Hz takes a different line than the
 * same cart cornering at 30Hz, because every integrator in the building is
 * first-order Euler and first-order Euler is step-dependent.
 *
 * The expensive reason is that it makes the game unverifiable. Four identical
 * headless runs of the same seeded script produced identical output three times
 * and a wedged player in a doorway the fourth, because one frame somewhere in
 * the middle took 40ms instead of 16ms, the dragged cart's catch-up step went
 * from three inches to eight, and eight inches was enough to put a shoulder
 * through a jamb it would otherwise have slid along. Nothing in the sim was
 * random. The machine's load was the random number generator.
 *
 * So: real time accumulates, and the simulation is stepped in whole ticks of
 * exactly STEP seconds. Two ticks in one frame see the same input, which is
 * correct — you did not press the key twice. A frame longer than MAX_CATCHUP
 * ticks drops the excess on the floor rather than spiralling, which is the same
 * protection the old clamp gave a backgrounded tab.
 *
 * Callbacks receive (dt, elapsed) as before; dt is now always STEP.
 */
export function createLoop(update, { step = 1 / 60, maxCatchup = 4 } = {}) {
  const STEP = step;
  const MAX_CATCHUP = maxCatchup;
  let rafId = null;
  let last = 0;
  let elapsed = 0;
  let debt = 0;

  function frame(now) {
    rafId = requestAnimationFrame(frame);
    advance((now - last) / 1000);
    last = now;
  }

  /**
   * Exported for the test, and for the headless harness, which drives whole
   * ticks directly so that a slow CI box and a fast laptop run the same shift.
   */
  function advance(seconds) {
    debt += realSeconds(seconds);
    let ticks = 0;
    while (debt >= STEP && ticks < MAX_CATCHUP) {
      debt -= STEP;
      elapsed += STEP;
      ticks += 1;
      update(STEP, elapsed);
    }
    // Whatever is left over after the catch-up cap is time the machine owes the
    // player and will never repay. Forget it rather than run it up.
    if (ticks >= MAX_CATCHUP) debt = 0;
    return ticks;
  }

  /** A rAF timestamp can predate the `performance.now()` taken inside start(). */
  function realSeconds(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return 0;
    return seconds;
  }

  return {
    advance,
    realSeconds,
    get step() {
      return STEP;
    },
    start() {
      if (rafId !== null) return;
      last = performance.now();
      debt = 0;
      rafId = requestAnimationFrame(frame);
    },
    stop() {
      if (rafId === null) return;
      cancelAnimationFrame(rafId);
      rafId = null;
    },
  };
}
